// Shared caret-position fix for controlled inputs that reformat a digit
// string by inserting literal separators (dashes) as the user types — e.g.
// phone numbers and ZIP+4. Used by PhoneNumberInput and SimpleTextField's
// `format` prop (ZIP).

/**
 * Finds the offset in `formatted` that sits immediately after the
 * `digitIndex`-th digit character, regardless of where separators fall.
 * @param {string} formatted - The already-formatted display string.
 * @param {number} digitIndex - Count of digits that should precede the caret.
 * @returns {number} The caret offset within `formatted`.
 */
function caretPositionAfterDigit(formatted: string, digitIndex: number): number {
  if (digitIndex <= 0) {
    return 0;
  }
  let digitsSeen = 0;
  for (const [index, character] of [...formatted].entries()) {
    if (/\d/.test(character)) {
      digitsSeen++;
      if (digitsSeen === digitIndex) {
        return index + 1;
      }
    }
  }
  return formatted.length;
}

/**
 * Reformats the DOM node's value via `format` and repositions the caret to
 * stay right after the digit the user just typed, instead of drifting past a
 * newly inserted separator. Returns the formatted string so callers can pass
 * it (or its stripped digits) to their `onChange`.
 * @param event - The native change event.
 * @param format - Formats a raw digit string into its display form.
 * @returns {string} The formatted value now applied to the DOM node.
 */
export function applyDigitFormatWithCaret(
  event: React.ChangeEvent<HTMLInputElement>,
  format: (raw: string) => string
): string {
  const inputElement = event.target;
  const raw = inputElement.value;
  const caretPosition = inputElement.selectionStart ?? raw.length;
  const formatted = format(raw);
  // When typing forward at the end of the value, the caret always belongs at
  // the end of whatever `format` produced — including a separator the user
  // just typed themselves that `format` chose to keep (e.g. a manually typed
  // ZIP+4 dash). The digit-count mapping below only tracks digits, so it
  // can't account for a trailing separator with no digit after it yet.
  const newCaretPosition =
    caretPosition >= raw.length
      ? formatted.length
      : caretPositionAfterDigit(
          formatted,
          Math.min(raw.slice(0, caretPosition).replaceAll(/\D/g, "").length, formatted.replaceAll(/\D/g, "").length)
        );

  inputElement.value = formatted;
  inputElement.setSelectionRange(newCaretPosition, newCaretPosition);

  // React restores the input's selection to whatever it was when this native
  // event first arrived — i.e. the browser's pre-reformat caret position —
  // as the last step of its own event dispatch, which runs after this
  // handler AND after any layout effect. That clobbers the correction above.
  // A microtask runs after that dispatch has finished, so it's the only
  // place left to reassert the right position.
  queueMicrotask(() => {
    if (inputElement.value === formatted) {
      inputElement.setSelectionRange(newCaretPosition, newCaretPosition);
    }
  });

  return formatted;
}
